import ValidateService from '../service/ValidateService';
import { handleLockApiError } from './lockErrorHandler';
import { showToastMessage } from './commonFunctions';

// ─── Message Formatting Helpers ───────────────────────────────────────────────

export const formatTestPlanCascadeSuccessMessage = (
  locked: boolean,
  changedPlans: number,
  changedCases: number,
  keptLocked: number
): string => {
  let msg = locked
    ? `Successfully locked ${changedPlans} Test Plan${changedPlans === 1 ? '' : 's'}`
    : `Successfully unlocked ${changedPlans} Test Plan${changedPlans === 1 ? '' : 's'}`;
  if (changedCases > 0) {
    msg += ` (${changedCases} test case${changedCases === 1 ? '' : 's'} cascaded)`;
  }
  if (keptLocked > 0) {
    msg += ` (${keptLocked} case${keptLocked === 1 ? '' : 's'} kept locked by other plans)`;
  }
  return msg;
};

export const formatTestPlanSingleSuccessMessage = (
  planName: string,
  targetLocked: boolean,
  keptLocked: number
): string => {
  let msg = targetLocked
    ? `Test Plan "${planName}" locked`
    : `Test Plan "${planName}" unlocked`;
  if (keptLocked > 0) {
    msg += ` (${keptLocked} case${keptLocked === 1 ? '' : 's'} kept locked by other plans)`;
  }
  return msg;
};

// ─── Test Cases Lock Handlers ─────────────────────────────────────────────────

export interface ExecuteTestCaseBulkLockOptions {
  missionId?: string;
  satelliteId?: string;
  selectedIds: string[];
  locked: boolean;
  setIsLocking?: (loading: boolean) => void;
  onSuccess?: () => any;
}

export const executeTestCaseBulkLock = async ({
  missionId,
  satelliteId,
  selectedIds,
  locked,
  setIsLocking,
  onSuccess,
}: ExecuteTestCaseBulkLockOptions): Promise<boolean> => {
  if (!missionId || !satelliteId || selectedIds.length === 0) return false;
  setIsLocking?.(true);
  try {
    await ValidateService.setTestCasesLock(missionId, satelliteId, selectedIds, locked);
    showToastMessage(
      'success',
      locked
        ? `Locked ${selectedIds.length} test case${selectedIds.length === 1 ? '' : 's'}`
        : `Unlocked ${selectedIds.length} test case${selectedIds.length === 1 ? '' : 's'}`
    );
    await onSuccess?.();
    return true;
  } catch (err: any) {
    handleLockApiError(err, `Failed to ${locked ? 'lock' : 'unlock'} test cases`);
    return false;
  } finally {
    setIsLocking?.(false);
  }
};

export interface ExecuteTestCaseSingleLockOptions {
  missionId?: string;
  satelliteId?: string;
  testCaseId: string;
  testCaseTitle: string;
  targetLocked: boolean;
  setIsLocking?: (loading: boolean) => void;
  onSuccess?: () => any;
}

export const executeTestCaseSingleLock = async ({
  missionId,
  satelliteId,
  testCaseId,
  testCaseTitle,
  targetLocked,
  setIsLocking,
  onSuccess,
}: ExecuteTestCaseSingleLockOptions): Promise<boolean> => {
  if (!missionId || !satelliteId || !testCaseId) return false;
  setIsLocking?.(true);
  try {
    await ValidateService.setTestCasesLock(missionId, satelliteId, [testCaseId], targetLocked);
    showToastMessage(
      'success',
      targetLocked ? `Test case "${testCaseTitle}" locked` : `Test case "${testCaseTitle}" unlocked`
    );
    await onSuccess?.();
    return true;
  } catch (err: any) {
    handleLockApiError(err, `Failed to ${targetLocked ? 'lock' : 'unlock'} test case`);
    return false;
  } finally {
    setIsLocking?.(false);
  }
};

// ─── Test Plan Lock Handlers ──────────────────────────────────────────────────

export interface ExecuteTestPlanBulkLockOptions {
  missionId?: string;
  satelliteId?: string;
  selectedPlanIds: string[];
  locked: boolean;
  setIsLocking?: (loading: boolean) => void;
  onSuccess?: () => any;
}

export const executeTestPlanBulkLock = async ({
  missionId,
  satelliteId,
  selectedPlanIds,
  locked,
  setIsLocking,
  onSuccess,
}: ExecuteTestPlanBulkLockOptions): Promise<boolean> => {
  if (!missionId || !satelliteId || selectedPlanIds.length === 0) return false;
  setIsLocking?.(true);
  try {
    const res: any = await ValidateService.setTestPlansLock(
      missionId,
      satelliteId,
      selectedPlanIds,
      locked
    );
    const resData = res?.data?.data;
    const changedPlans = resData?.testPlans?.changed ?? selectedPlanIds.length;
    const changedCases = resData?.testCases?.changed ?? 0;
    const keptLocked = resData?.keptLockedBySharedPlans ?? 0;

    const msg = formatTestPlanCascadeSuccessMessage(locked, changedPlans, changedCases, keptLocked);
    showToastMessage('success', msg);
    await onSuccess?.();
    return true;
  } catch (err: any) {
    handleLockApiError(err, `Failed to ${locked ? 'lock' : 'unlock'} test plans`);
    return false;
  } finally {
    setIsLocking?.(false);
  }
};

export interface ExecuteTestPlanSingleLockOptions {
  missionId?: string;
  satelliteId?: string;
  testPlanId: string;
  testPlanName: string;
  targetLocked: boolean;
  setIsLocking?: (loading: boolean) => void;
  onSuccess?: () => any;
}

export const executeTestPlanSingleLock = async ({
  missionId,
  satelliteId,
  testPlanId,
  testPlanName,
  targetLocked,
  setIsLocking,
  onSuccess,
}: ExecuteTestPlanSingleLockOptions): Promise<boolean> => {
  if (!missionId || !satelliteId || !testPlanId) return false;
  setIsLocking?.(true);
  try {
    const res: any = await ValidateService.setTestPlansLock(
      missionId,
      satelliteId,
      [testPlanId],
      targetLocked
    );
    const resData = res?.data?.data;
    const keptLocked = resData?.keptLockedBySharedPlans ?? 0;
    const msg = formatTestPlanSingleSuccessMessage(testPlanName, targetLocked, keptLocked);
    showToastMessage('success', msg);
    await onSuccess?.();
    return true;
  } catch (err: any) {
    handleLockApiError(err, `Failed to ${targetLocked ? 'lock' : 'unlock'} test plan`);
    return false;
  } finally {
    setIsLocking?.(false);
  }
};

// ─── Validation Configs (Telemetry) Handlers ──────────────────────────────────

export interface ExecuteValidationConfigBulkLockOptions {
  missionId?: string;
  satelliteId?: string;
  selectedConfigIds: string[];
  locked: boolean;
  setIsLocking?: (loading: boolean) => void;
  onSuccess?: () => any;
}

export const executeValidationConfigBulkLock = async ({
  missionId,
  satelliteId,
  selectedConfigIds,
  locked,
  setIsLocking,
  onSuccess,
}: ExecuteValidationConfigBulkLockOptions): Promise<boolean> => {
  if (!missionId || !satelliteId || selectedConfigIds.length === 0) return false;
  setIsLocking?.(true);
  try {
    await ValidateService.setValidationConfigsLock(
      missionId,
      satelliteId,
      selectedConfigIds,
      locked
    );
    showToastMessage(
      'success',
      locked
        ? `Locked ${selectedConfigIds.length} validation config${selectedConfigIds.length === 1 ? '' : 's'}`
        : `Unlocked ${selectedConfigIds.length} validation config${selectedConfigIds.length === 1 ? '' : 's'}`
    );
    await onSuccess?.();
    return true;
  } catch (err: any) {
    handleLockApiError(err, `Failed to ${locked ? 'lock' : 'unlock'} validation configs`);
    return false;
  } finally {
    setIsLocking?.(false);
  }
};

export interface ExecuteValidationConfigSingleLockOptions {
  missionId?: string;
  satelliteId?: string;
  configId: string;
  configName: string;
  targetLocked: boolean;
  setIsLocking?: (loading: boolean) => void;
  onSuccess?: () => any;
}

export const executeValidationConfigSingleLock = async ({
  missionId,
  satelliteId,
  configId,
  configName,
  targetLocked,
  setIsLocking,
  onSuccess,
}: ExecuteValidationConfigSingleLockOptions): Promise<boolean> => {
  if (!missionId || !satelliteId || !configId) return false;
  setIsLocking?.(true);
  try {
    await ValidateService.setValidationConfigsLock(
      missionId,
      satelliteId,
      [configId],
      targetLocked
    );
    showToastMessage(
      'success',
      targetLocked
        ? `Validation config "${configName}" locked`
        : `Validation config "${configName}" unlocked`
    );
    await onSuccess?.();
    return true;
  } catch (err: any) {
    handleLockApiError(err, `Failed to ${targetLocked ? 'lock' : 'unlock'} validation config`);
    return false;
  } finally {
    setIsLocking?.(false);
  }
};

// ─── Setup & Session Handlers ─────────────────────────────────────────────────

export interface ExecuteSetupLockOptions {
  missionId?: string;
  groupId?: string;
  satelliteId?: string;
  setupId: string;
  setupName: string;
  targetLocked: boolean;
  setIsLocking?: (loading: boolean) => void;
  onSuccess?: () => any;
}

export const executeSetupLock = async ({
  missionId,
  groupId,
  satelliteId,
  setupId,
  setupName,
  targetLocked,
  setIsLocking,
  onSuccess,
}: ExecuteSetupLockOptions): Promise<boolean> => {
  if (!missionId || !setupId) return false;
  setIsLocking?.(true);
  try {
    await ValidateService.setSetupLock(
      missionId,
      groupId || '',
      satelliteId || '',
      setupId,
      targetLocked
    );
    showToastMessage(
      'success',
      targetLocked ? `Setup "${setupName}" locked` : `Setup "${setupName}" unlocked`
    );
    await onSuccess?.();
    return true;
  } catch (err: any) {
    handleLockApiError(err, `Failed to ${targetLocked ? 'lock' : 'unlock'} setup`);
    return false;
  } finally {
    setIsLocking?.(false);
  }
};


export interface ExecuteSessionLockOptions {
  missionId?: string;
  sessionId: string;
  sessionName: string;
  targetLocked: boolean;
  setIsLocking?: (loading: boolean) => void;
  onSuccess?: () => any;
}

export const executeSessionLock = async ({
  missionId,
  sessionId,
  sessionName,
  targetLocked,
  setIsLocking,
  onSuccess,
}: ExecuteSessionLockOptions): Promise<boolean> => {
  if (!missionId || !sessionId) return false;
  setIsLocking?.(true);
  try {
    await ValidateService.setValidationSessionsLock(missionId, [sessionId], targetLocked);
    showToastMessage(
      'success',
      targetLocked ? `Session "${sessionName}" locked` : `Session "${sessionName}" unlocked`
    );
    await onSuccess?.();
    return true;
  } catch (err: any) {
    handleLockApiError(err, `Failed to ${targetLocked ? 'lock' : 'unlock'} session`);
    return false;
  } finally {
    setIsLocking?.(false);
  }
};

// ─── Group-Level Lock Handlers ────────────────────────────────────────────────

export interface ExecuteSatelliteGroupValidationLockOptions {
  missionId: string;
  satelliteGroupId: string;
  locked: boolean;
  setIsLocking?: (loading: boolean) => void;
  onSuccess?: () => any;
}

export interface ExecuteSatelliteGroupValidationLockAllOptions {
  missionId: string;
  satelliteGroupId: string;
  locked: boolean;
  setIsLocking?: (loading: boolean) => void;
  onSuccess?: () => any;
}

export const executeSatelliteGroupValidationLockAll = async ({
  missionId,
  satelliteGroupId,
  locked,
  setIsLocking,
  onSuccess,
}: ExecuteSatelliteGroupValidationLockAllOptions): Promise<boolean> => {
  if (!missionId || !satelliteGroupId) return false;
  setIsLocking?.(true);
  try {
    let executed = false;
    // 1. Attempt unified endpoint first (if backend implemented it)
    try {
      await ValidateService.setSatelliteGroupValidationLockAll(missionId, satelliteGroupId, locked);
      executed = true;
    } catch (unifiedErr: any) {
      const status = unifiedErr?.response?.status;
      // If 404/405/501 or route not found on backend, gracefully fall back to the 2-call model
      if (status === 404 || status === 405 || status === 501 || !unifiedErr?.response) {
        // Dual-call model: 1. project-service validation-lock, 2. test-service sessions lock
        await ValidateService.setSatelliteGroupValidationLock(missionId, satelliteGroupId, locked);
        try {
          await ValidateService.setSatelliteGroupSessionsLock(missionId, satelliteGroupId, locked);
        } catch (sessionErr) {
          console.warn('Session lock failed during group lock-all fallback:', sessionErr);
        }
        executed = true;
      } else {
        throw unifiedErr;
      }
    }

    if (executed) {
      showToastMessage(
        'success',
        locked
          ? 'Locked all validation items and sessions for satellite group'
          : 'Unlocked all validation items and sessions for satellite group'
      );
      await onSuccess?.();
      return true;
    }
    return false;
  } catch (err: any) {
    handleLockApiError(err, `Failed to ${locked ? 'lock' : 'unlock'} satellite group validation items`);
    return false;
  } finally {
    setIsLocking?.(false);
  }
};

export const executeSatelliteGroupValidationLock = async ({
  missionId,
  satelliteGroupId,
  locked,
  setIsLocking,
  onSuccess,
}: ExecuteSatelliteGroupValidationLockOptions): Promise<boolean> => {
  if (!missionId || !satelliteGroupId) return false;
  setIsLocking?.(true);
  try {
    await ValidateService.setSatelliteGroupValidationLock(missionId, satelliteGroupId, locked);
    showToastMessage(
      'success',
      locked
        ? 'Locked all validation items for satellite group'
        : 'Unlocked all validation items for satellite group'
    );
    await onSuccess?.();
    return true;
  } catch (err: any) {
    handleLockApiError(err, `Failed to ${locked ? 'lock' : 'unlock'} satellite group validation items`);
    return false;
  } finally {
    setIsLocking?.(false);
  }
};

export interface ExecuteSatelliteGroupSessionsLockOptions {
  missionId: string;
  satelliteGroupId: string;
  locked: boolean;
  setIsLocking?: (loading: boolean) => void;
  onSuccess?: () => any;
}

export const executeSatelliteGroupSessionsLock = async ({
  missionId,
  satelliteGroupId,
  locked,
  setIsLocking,
  onSuccess,
}: ExecuteSatelliteGroupSessionsLockOptions): Promise<boolean> => {
  if (!missionId || !satelliteGroupId) return false;
  setIsLocking?.(true);
  try {
    await ValidateService.setSatelliteGroupSessionsLock(missionId, satelliteGroupId, locked);
    showToastMessage(
      'success',
      locked
        ? 'Locked all sessions for satellite group'
        : 'Unlocked all sessions for satellite group'
    );
    await onSuccess?.();
    return true;
  } catch (err: any) {
    handleLockApiError(err, `Failed to ${locked ? 'lock' : 'unlock'} satellite group sessions`);
    return false;
  } finally {
    setIsLocking?.(false);
  }
};
